<template>
    <div class="na-post-detail-poll" v-if="!isLoading">
        <h2 v-if="isShowTitle" class="title is-4">{{ dataSupplemental.title }}</h2>
        
        <!-- Show results if user has voted or is not authenticated -->
        <div v-if="!userIsLoggedIn || hasVoted" class="poll-results">
            <h2 v-if="getTotalVotesAllQuestions() > 0" class="title is-4">{{ getTotalVotesAllQuestions() }} Total Votes</h2>
            <div v-for="(question, index) in dataSupplemental.questions" :key="index" class="question-results mb-4">
                <h3 class="subtitle is-5 mb-2">{{ question.text }}</h3>
                
                <!-- Multiple Choice Results -->
                <div v-if="question.type === 'multiple_choice'" class="choices-results">
                    <div v-for="(choice, choiceIndex) in question.choices" :key="choiceIndex" class="choice-result mb-2">
                        <div class="level is-mobile mb-0">
                            <div class="level-left">
                                <div class="level-item">
                                    <span>{{ choice }}</span>
                                </div>
                            </div>
                            <div class="level-right">
                                <div class="level-item">
                                    <span class="has-text-weight-semibold">{{ getChoicePercentage(question, choiceIndex) }}%</span>
                                    <span class="has-text-grey ml-2">({{ getChoiceVotes(question, choiceIndex) }} votes)</span>
                                </div>
                            </div>
                        </div>
                        <progress 
                            class="progress mt-0" 
                            :value="getChoicePercentage(question, choiceIndex)" 
                            max="100"
                        ></progress>
                    </div>
                    <!-- Other Option Results -->
                    <div v-if="question.hasOtherOption" class="choice-result mb-2">
                        <div class="level is-mobile mb-0">
                            <div class="level-left">
                                <div class="level-item">
                                    <span>Other</span>
                                </div>
                            </div>
                            <div class="level-right">
                                <div class="level-item">
                                    <span class="has-text-weight-semibold">{{ getOtherPercentage(question) }}%</span>
                                    <span class="has-text-grey ml-2">({{ getOtherVotes(question) }} votes)</span>
                                </div>
                            </div>
                        </div>
                        <progress 
                            class="progress" 
                            :value="getOtherPercentage(question)" 
                            max="100"
                        ></progress>
                    </div>
                </div>

                <!-- Text Response Results -->
                <div v-else-if="question.type === 'free_text'" class="text-responses">
                    <div v-for="(response, responseIndex) in getTextResponses(question)" :key="responseIndex" class="text-response mb-2">
                        <div class="box">
                            <p>{{ response }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Show voting form if user is authenticated and hasn't voted -->
        <div v-else class="poll-voting">
            <Error :errors="errors" />
            <form @submit.prevent="submitVote">
                <div v-for="(question, index) in dataSupplemental.questions" :key="index" class="question-voting mb-4">
                    <h3 class="subtitle is-5 mb-2">Question {{ index + 1 }}: {{ question.text }}</h3>
                    
                    <!-- Multiple Choice Input -->
                    <div v-if="question.type === 'multiple_choice'" class="choices-input">
                        <div v-for="(choice, choiceIndex) in question.choices" :key="choiceIndex" class="control mb-2">
                            <label class="radio">
                                <input 
                                    type="radio" 
                                    :name="`question-${index}`" 
                                    :value="choiceIndex"
                                    v-model="responses[index]"
                                    required
                                >
                                {{ choice }}
                            </label>
                        </div>
                        <!-- Other Option Input -->
                        <div v-if="question.hasOtherOption" class="control mb-2">
                            <label class="radio">
                                <input 
                                    type="radio" 
                                    :name="`question-${index}`" 
                                    value="other"
                                    v-model="responses[index]"
                                    required
                                >
                                Other
                            </label>
                            <div v-if="responses[index] === 'other'" class="ml-4 mt-2">
                                <textarea 
                                    class="textarea" 
                                    :placeholder="'Please specify...'"
                                    v-model="otherResponses[index]"
                                    required
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    <!-- Text Response Input -->
                    <div v-else-if="question.type === 'free_text'" class="text-input">
                        <div class="field">
                            <div class="control">
                                <textarea 
                                    class="textarea" 
                                    :placeholder="'Enter your response'"
                                    v-model="responses[index]"
                                    required
                                ></textarea>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="field">
                    <div class="control">
                        <button 
                            type="submit" 
                            class="button is-primary"
                            :class="{ 'is-loading': isSubmitting }"
                            :disabled="isSubmitting"
                        >
                            Submit Vote
                        </button>
                    </div>
                </div>
            </form>
        </div>

        <!-- Poll Status -->
        <div class="poll-status mt-4">
            <p v-if="dataSupplemental.ends_at" class="has-text-grey">
                Poll ends: {{ formatDate(dataSupplemental.ends_at) }}
            </p>
            <p v-if="!userIsLoggedIn" class="has-text-info">
                Please log in to vote in this poll
            </p>
            <p v-else-if="hasVoted" class="has-text-success">
                You have already voted in this poll
                <span v-if="isPollOpen()" class="ml-2">
                    (<a href="#" @click.prevent="unvote" class="has-text-danger" :class="{ 'is-loading': isLoading }">unvote</a>)
                </span>
            </p>
        </div>
    </div>
</template>

<script>
import { formatDateTime } from '@/helpers/date.js';
import { getPollStatus, vote, unvote } from '@/services/poll.js';
import Error from '@/components/Common/Error.vue';

export default {
    name: 'PollViewer',

    components: {
        Error
    },

    props: {
        post: {
            type: Object,
            required: true
        },
        dataSupplemental: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            responses: [],
            otherResponses: [],
            hasVoted: false,
            isSubmitting: false,
            pollResults: null,
            isLoading: false,
            errors: [],
            isShowTitle: false,
        };
    },

    methods: {
        formatDate(date) {
            return formatDateTime(date);
        },

        async checkVoteStatus() {
            this.errors = [];
            if (!this.userIsLoggedIn) return;

            this.isLoading = true;

            try {
                const response = await getPollStatus({
                    data: {
                        post_id: this.post.id
                    }
                });
                this.hasVoted = response.hasVoted;
                this.pollResults = response.results;
            } catch (error) {
                console.error('Error checking vote status:', error);
                this.errors = [ 'Failed to check vote status. Please try again.' ];
            } finally {
                this.isLoading = false;
            }
        },

        getChoiceVotes(question, choiceIndex) {
            if (!this.pollResults) return 0;
            const questionResults = this.pollResults[question.id];
            if (!questionResults) return 0;
            return questionResults.choices[choiceIndex] || 0;
        },

        getChoicePercentage(question, choiceIndex) {
            const votes = this.getChoiceVotes(question, choiceIndex);
            const totalVotes = this.getTotalVotes(question);
            if (totalVotes === 0) return 0;
            return Math.round((votes / totalVotes) * 100);
        },

        getTotalVotes(question) {
            if (!this.pollResults) return 0;
            const questionResults = this.pollResults[question.id];
            if (!questionResults) return 0;
            return questionResults.totalVotes || 0;
        },

        getTotalVotesAllQuestions() {
            if (!this.pollResults) return 0;
            return Object.values(this.pollResults).reduce((acc, question) => acc + question.totalVotes, 0);
        },

        getTextResponses(question) {
            if (!this.pollResults) return [];
            const questionResults = this.pollResults[question.id];
            if (!questionResults) return [];
            return questionResults.otherResponses || [];
        },

        getOtherVotes(question) {
            if (!this.pollResults) return 0;
            const questionResults = this.pollResults[question.id];
            if (!questionResults) return 0;
            return questionResults.otherVotes || 0;
        },

        getOtherPercentage(question) {
            const votes = this.getOtherVotes(question);
            const totalVotes = this.getTotalVotes(question);
            if (totalVotes === 0) return 0;
            return Math.round((votes / totalVotes) * 100);
        },

        async submitVote() {
            if (!this.userIsLoggedIn || this.hasVoted) return;
            this.errors = [];

            this.isLoading = true;
            
            this.isSubmitting = true;
            try {
                const response = await vote({
                    data: {
                        post_id: this.post.id,
                        responses: this.responses,
                        otherResponses: this.otherResponses
                    }
                });
                
                this.hasVoted = true;
                this.pollResults = response.results;
            } catch (error) {
                console.error('Error submitting vote:', error);
                if (error.response && error.response.status === 401) {
                    this.errors = ['Please log in to vote in this poll'];
                } else {
                    this.errors = ['Failed to submit vote. Please try again.'];
                }
            } finally {
                this.isSubmitting = false;
                this.isLoading = false;
            }
        },

        isPollOpen() {
            if (!this.dataSupplemental.ends_at) return true;
            return new Date(this.dataSupplemental.ends_at) > new Date();
        },

        async unvote() {
            if (!this.userIsLoggedIn || !this.hasVoted || !this.isPollOpen()) return;
            this.errors = [];

            this.isLoading = true;
            
            try {
                const response = await unvote({
                    data: {
                        post_id: this.post.id
                    }
                });
                
                this.hasVoted = false;
                this.pollResults = response.results;
                this.responses = [];
                this.otherResponses = [];
            } catch (error) {
                console.error('Error unvoting:', error);
                if (error.response && error.response.status === 401) {
                    this.errors = ['Please log in to unvote in this poll'];
                } else if (error.response && error.response.status === 400) {
                    this.errors = [error.response.data.error || 'Failed to unvote. Please try again.'];
                } else {
                    this.errors = ['Failed to unvote. Please try again.'];
                }
            } finally {
                this.isLoading = false;
            }
        }
    },

    mounted() {
        this.checkVoteStatus();
    }
};
</script>

<style scoped>
.na-post-detail-poll {
    /* max-width: 800px; */
    margin: 0 auto;
}

/*
.question-results,
.question-voting {
    background: #f5f5f5;
    padding: 1.5rem;
    border-radius: 4px;
}
*/

.choice-result {
    margin-bottom: 1rem;
}

.progress {
    margin-top: 0.5rem;
}

.text-response {
    margin-bottom: 1rem;
}

.text-response .box {
    background: white;
    padding: 1rem;
}

.poll-status {
    text-align: center;
    font-size: 0.9rem;
}
</style>
